import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/* ──────────────────────────────────────────────────────────────────────────
   Instructors — shared list normaliser + the "By <name>" chip row.

   A course or group carries an ORDERED LIST of instructors. The API sends
   `instructors: [...]`; the older singular `instructor` key is still populated
   with the first one, so `instructorsOf()` collapses both into one array and
   every call site gates on `list.length` instead of `x.instructor`.

   Unlike the landing-page templates (which each style their instructor section
   themselves), these chips are one small, repeated UI element — so the markup
   is shared here rather than copied into five screens.
   ────────────────────────────────────────────────────────────────────────── */

/**
 * Ordered instructor list for an entity, with the legacy singular key as the
 * fallback for rows saved before multi-instructor support.
 */
export function instructorsOf(entity) {
  const list = Array.isArray(entity?.instructors)
    ? entity.instructors.filter((i) => i && i.name)
    : [];
  if (list.length) return list;
  if (entity?.instructor && entity.instructor.name) return [entity.instructor];
  // Course rows carry the instructor flattened onto the row rather than nested
  // (instructor_name / instructor_image) — same legacy fallback, different shape.
  if (entity?.instructor_name) {
    return [{
      instructor_id: entity.instructor_id || null,
      name: entity.instructor_name,
      profile_image: entity.instructor_image || "",
    }];
  }
  return [];
}

/** "Ada" · "Ada & Grace" · "Ada, Grace +2" */
export function instructorNames(list, max = 2) {
  if (!list || !list.length) return "";
  const names = list.map((i) => i.name);
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names.slice(0, max).join(", ")} +${names.length - max}`;
}

/**
 * One avatar. `imgClass` / `initialClass` / `initialStyle` let a caller keep its
 * own avatar treatment (ring, tinted fill) instead of the default brand-filled
 * circle — the screens differ here and shouldn't be flattened into one look.
 */
function Avatar({
  ins,
  primary,
  sizeClass = "w-6 h-6",
  textClass = "text-[10px]",
  imgClass = "",
  initialClass = "",
  initialStyle,
}) {
  if (ins.profile_image) {
    return (
      <img
        src={ins.profile_image}
        alt={ins.name}
        className={`${sizeClass} rounded-full object-cover ${imgClass}`}
        onError={(e) => (e.currentTarget.style.display = "none")}
      />
    );
  }
  return (
    <span
      className={`${sizeClass} ${textClass} rounded-full flex items-center justify-center font-bold ${initialClass || "text-white"}`}
      style={initialStyle || { backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
    >
      {(ins.name || "?").charAt(0).toUpperCase()}
    </span>
  );
}

/** Right-pointing chevron — every panel row opens that person's page. */
function RowArrow() {
  return (
    <svg
      viewBox="0 0 20 20"
      aria-hidden="true"
      fill="none"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="ml-auto h-3.5 w-3.5 shrink-0 stroke-current text-white/40 transition group-hover/row:translate-x-0.5 group-hover/row:text-white/90"
    >
      <path d="M7.5 4.5 13 10l-5.5 5.5" />
    </svg>
  );
}

/**
 * A chip's click target.
 *
 * `asSpan` renders role="button" instead of <button>. Some of these chips sit
 * inside a course card that is ITSELF a <button>, and a nested <button> is
 * invalid HTML — the parser closes the outer one and the card stops working.
 * The hover panel never needs this: it is portalled to <body>, outside the
 * card, so its rows stay real buttons.
 */
function ClickTarget({ asSpan, onActivate, className, title, style, children }) {
  // stopPropagation: opening an instructor is not the same action as opening
  // the card this chip sits on.
  const fire = (e) => {
    e.stopPropagation();
    onActivate();
  };
  if (!asSpan) {
    return (
      <button type="button" onClick={fire} title={title} className={className} style={style}>
        {children}
      </button>
    );
  }
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={fire}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          fire(e);
        }
      }}
      title={title}
      className={className}
      style={style}
    >
      {children}
    </span>
  );
}

/**
 * The instructor chip(s) for a card or landing header.
 *
 * One instructor renders exactly as before: avatar + "By <name>" in a single
 * chip. Several render as an overlapping avatar stack with NO label — a card
 * chip only fits a few characters, so a combined label could only ever be
 * "Test instructor, Alwin Jacob +1", an ellipsis hiding the very names it is
 * there to give. The names move into a hover panel.
 *
 * That panel is a MENU, not a label: every row is a link to that instructor's
 * page, with a chevron saying so. Two consequences —
 *
 *   · it must stay open while the pointer travels off the chip and into it, so
 *     hiding is deferred and any re-entry (pointer or focus, chip or panel)
 *     cancels the pending hide;
 *   · a transparent bridge covers the gap between chip and panel, so pausing
 *     mid-travel doesn't close it either.
 *
 * @param {object[]} list        from instructorsOf()
 * @param {function} onInstructor  (instructorId) => void
 * @param {string}   primary     brand colour for initial-avatars
 * @param {string}   className   chip classes supplied by the caller's design
 * @param {string}   prefix      label prefix ("By" / "Hosted by"), always in the tooltip
 * @param {boolean}  prefixInLabel  show the prefix in the visible text too
 * @param {number}   maxAvatars  avatars shown before collapsing to +N
 * @param {string}   panelLabel  header of the multi-instructor hover panel
 * @param {boolean}  insideButton  the chip sits inside a <button> card — see ClickTarget
 */
export function InstructorChips({
  list,
  onInstructor,
  primary,
  className = "",
  prefix = "By",
  prefixInLabel = true,
  maxAvatars = 3,
  sizeClass = "w-6 h-6",
  textClass = "text-[10px]",
  labelClass = "truncate",
  imgClass = "",
  initialClass = "",
  initialStyle,
  panelLabel = "",
  insideButton = false,
}) {
  // Anchor + position for the hover panel. It is portalled to <body> and placed
  // from the chip's rect: these chips sit inside cards that clip their overflow,
  // so an absolutely-positioned panel would be sliced off at the card edge.
  const anchorRef = useRef(null);
  const [tipPos, setTipPos] = useState(null);
  const hideTimer = useRef(null);
  useEffect(() => () => clearTimeout(hideTimer.current), []);

  if (!list || !list.length) return null;

  const names = instructorNames(list);
  const label = prefixInLabel ? `${prefix} ${names}` : names;
  const title = `${prefix} ${names}`;

  // Without an onInstructor handler the chip is decoration, not navigation — it
  // must stay a plain span, and the panel rows stay plain rows.
  const interactive = typeof onInstructor === "function";

  const cancelHide = () => {
    clearTimeout(hideTimer.current);
    hideTimer.current = null;
  };
  // Dismiss on click as well as navigate: the panel is fixed-positioned, so it
  // would otherwise hang over whatever the click just opened.
  const openInstructor = (id) => {
    cancelHide();
    setTipPos(null);
    onInstructor(id);
  };

  if (list.length === 1) {
    const ins = list[0];
    const avatar = (
      <Avatar ins={ins} primary={primary} sizeClass={sizeClass} textClass={textClass} imgClass={imgClass} initialClass={initialClass} initialStyle={initialStyle} />
    );
    // A legacy row flattened onto the course carries a name but no id — there is
    // no page to open, so it renders as the plain label it always was.
    if (!interactive || !ins.instructor_id) {
      return (
        <span className={className} title={title}>
          {avatar}
          <span className={labelClass}>{label}</span>
        </span>
      );
    }
    return (
      <ClickTarget
        asSpan={insideButton}
        onActivate={() => openInstructor(ins.instructor_id)}
        title={title}
        className={className}
      >
        {avatar}
        <span className={labelClass}>{label}</span>
      </ClickTarget>
    );
  }

  const shown = list.slice(0, maxAvatars);
  const extra = list.length - shown.length;

  // "Hosted by" names a group's hosts; everything else credits course teaching.
  const hosted = prefix === "Hosted by";
  const heading = panelLabel || (hosted ? "Hosts" : "Instructors");

  const showTip = () => {
    cancelHide();
    const r = anchorRef.current?.getBoundingClientRect();
    if (r) setTipPos({ x: r.left + r.width / 2, y: r.top });
  };
  const hideTip = () => {
    cancelHide();
    hideTimer.current = setTimeout(() => setTipPos(null), 160);
  };

  return (
    <span
      ref={anchorRef}
      className={className}
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
      // Focus events from the portal bubble up this React tree, so tabbing into
      // a panel row lands here as onFocus and keeps the panel open.
      onFocus={showTip}
      onBlur={hideTip}
      tabIndex={0}
      aria-label={`${heading}: ${list.map((i) => i.name).join(", ")}`}
    >
      {/* Overlap is deliberately shallow. At -space-x-2 the next avatar's ring
          reached past the centre of the one beneath it and swallowed its
          initial, so a stack of initial-avatars read as blank crescents. */}
      <span className="flex items-center -space-x-1">
        {shown.map((ins, i) => {
          const avatar = (
            <Avatar ins={ins} primary={primary} sizeClass={sizeClass} textClass={textClass} imgClass={imgClass} initialClass={initialClass} initialStyle={initialStyle} />
          );
          const stackClass = "rounded-full ring-2 ring-surface";
          const key = ins.instructor_id || `${ins.name}-${i}`;
          const style = { zIndex: shown.length - i };
          return interactive && ins.instructor_id ? (
            <ClickTarget
              key={key}
              asSpan={insideButton}
              onActivate={() => openInstructor(ins.instructor_id)}
              title={ins.name}
              // A span needs the flex box a <button> gets for free, or the
              // avatar picks up a descender gap inside its ring.
              className={`${stackClass} transition hover:z-10 hover:scale-110 ${insideButton ? "inline-flex" : ""}`}
              style={style}
            >
              {avatar}
            </ClickTarget>
          ) : (
            <span key={key} title={ins.name} className={stackClass} style={style}>
              {avatar}
            </span>
          );
        })}
        {extra > 0 ? (
          <span
            className={`${sizeClass} ${textClass} rounded-full ring-2 ring-surface flex items-center justify-center font-bold bg-gray-200 text-gray-600`}
          >
            +{extra}
          </span>
        ) : null}
      </span>
      {tipPos
        ? createPortal(
            <span
              style={{
                position: "fixed",
                left: tipPos.x,
                top: tipPos.y - 10,
                transform: "translate(-50%, -100%)",
                zIndex: 9999,
                minWidth: "12rem",
                maxWidth: "17rem",
              }}
              className="block rounded-xl border border-white/10 bg-gradient-to-b from-[#1c222f] to-[#0c1018] p-2 text-left shadow-[0_22px_46px_-16px_rgba(0,0,0,0.8)] backdrop-blur-md"
              onMouseEnter={cancelHide}
              onMouseLeave={hideTip}
            >
              <span className="flex items-center gap-2 px-1 pb-1.5 text-[10px] font-extrabold uppercase tracking-[0.09em] text-white/55">
                {heading}
                <span className="ml-auto rounded-full bg-white/10 px-1.5 text-[10px] font-extrabold text-white/70">
                  {list.length}
                </span>
              </span>
              {list.map((ins, i) => {
                const key = ins.instructor_id || `${ins.name}-${i}`;
                const body = (
                  <>
                    {/* The panel has its own dark surface, so the avatars use a
                        neutral fill rather than the caller's card-tuned one — a
                        pale brand tint meant for a white card goes unreadable
                        here. */}
                    <Avatar
                      ins={ins}
                      primary={primary}
                      sizeClass="w-6 h-6 shrink-0"
                      textClass="text-[10px]"
                      initialStyle={{ backgroundColor: "rgba(255,255,255,0.16)", color: "rgb(var(--c-surface))" }}
                    />
                    <span className="truncate text-[12px] font-semibold leading-tight text-white">
                      {ins.name}
                    </span>
                  </>
                );
                return interactive && ins.instructor_id ? (
                  <button
                    key={key}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openInstructor(ins.instructor_id);
                    }}
                    title={`Open ${ins.name}`}
                    className="group/row flex w-full cursor-pointer items-center gap-2 rounded-lg px-1 py-1 text-left transition hover:bg-white/10"
                  >
                    {body}
                    <RowArrow />
                  </button>
                ) : (
                  <span key={key} className="flex items-center gap-2 rounded-lg px-1 py-1">
                    {body}
                  </span>
                );
              })}
              {/* Bridges the gap between chip and panel so the pointer can cross
                  it — or stop in it — without the panel counting that as a leave. */}
              <span aria-hidden="true" className="absolute inset-x-0 top-full h-2.5" />
              <span className="absolute left-1/2 top-full h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 border-b border-r border-white/10 bg-[#0c1018]" />
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}
